/**
 * AimLine.js
 */

import * as PIXI from "pixi.js";

import { Game, GameDisplayContainer, GameSprite, GameAnimatedSprite } from "pixi_core";
import { Common } from "./Common";

import {
    BubbleColor,
    SceneLayers,
    GameEvents,
    SHOOTER_X_POSITION,
    SHOOTER_Y_POSITION,
    AIMLINE_DOTS_SPRITES,
    AIMLINE_DOTS_SPACING,
    AIMLINE_DOTS_SPEED,
    AIMLINE_DOTS_FADE_DISTANCE,
    AIMLINE_NB_DOTS,
    AIMLINE_NB_LASER_SEGMENTS,
    AIMLINE_NB_PATCHES,
    AIMLINE_TARGET_INDICATORS,
    AIMLINE_LASER_SEGMENT_LENGTH,
    AIMLINE_LAST_LASER_SEGMENT_TRUNCATE,
    AIMLINE_LASER_SEGMENT_THRESHOLD,
    BUBBLE_COLOR_ID_TO_NAME,
    BUBBLE_TINTS
} from "../Constants";

export class AimLine {
    /**
     * @public
     */
    constructor() {
        /** @private @type {BubbleColor} */
        this._currentColor = BubbleColor.eBLUE;

        /** @private @type {GameDisplayContainer} */
        this._root = new GameDisplayContainer(Common.scene, SceneLayers.eAIM_LINE);
        this._root.visible = false;

        /** @private @type {GameDisplayContainer} */
        this._rootDots = new GameDisplayContainer();
        this._rootDots.visible = true;

        /** @private @type {GameDisplayContainer} */
        this._rootAllOut = new GameDisplayContainer();
        this._rootAllOut.visible = false;

        this._root.addChild(this._rootDots);
        this._root.addChild(this._rootAllOut);

        /** @private @type {BubbleColor} - Color of the current bubble inside the shooter*/
        this._color = BubbleColor.eRED;

        /** @private @type {{sprite: GameSprite, distance: Number}[]} */
        this._dots = [];

        // Create a pool of dots
        for (let i = 0; i < AIMLINE_NB_DOTS; ++i) {
            const dot = {
                sprite: new GameSprite(AIMLINE_DOTS_SPRITES[0]),
                distance: i * AIMLINE_DOTS_SPACING
            };
            this._dots.push(dot);
            this._rootDots.addChild(dot.sprite);
        }

        /** @private @type {Number} */
        this._maxDotDistance = AIMLINE_NB_DOTS * AIMLINE_DOTS_SPACING;

        /** @private @type {{p1: {x: Number, y: Number, distance: Number, cos: Number, sin: Number}, p2: {x: Number, y: Number, distance: Number, cos: Number, sin: Number}, totalDistance: Number}} */
        this._dotsPath = {
            p1: { x: 0, y: 0, distance: 0, cos: 0, sin: 0 },
            p2: { x: 0, y: 0, distance: 0, cos: 0, sin: 0 },
            totalDistance: 0
        };

        /** @private @type {Boolean} */
        this._allOutActive = false;

        /** @private @type {GameAnimatedSprite[]} */
        this._lasers = [];

        /** @private @type {Number} */
        this._laserSegmentIndex = 0;

        // Create a pool of laser segments
        const laserData = Game.design.getGraphicData("Laser");
        for (let i = 0; i < AIMLINE_NB_LASER_SEGMENTS; ++i) {
            const laserSegment = new GameAnimatedSprite(null, laserData);
            laserSegment.stop();
            laserSegment.visible = false;

            this._lasers.push(laserSegment);
            this._rootAllOut.addChild(laserSegment);
        }

        /** @private @type {GameSprite[]} */
        this._patches = [];

        // Create a pool of laser patches
        const patchData = Game.design.getGraphicData("LaserPatch");
        for (let i = 0; i < AIMLINE_NB_PATCHES; ++i) {
            const patch = new GameSprite(patchData);
            this._patches.push(patch);
            this._rootAllOut.addChild(patch);
        }

        /** @private @type {Number} */
        this._patchIndex = 0;

        /** @private @type {GameSprite} */
        this._visor = new GameSprite(Game.design.getGraphicData("LaserVisor"));
        this._visor.x = SHOOTER_X_POSITION;
        this._visor.y = SHOOTER_Y_POSITION;
        this._rootAllOut.addChild(this._visor);

        /** @private @type {GameAnimatedSprite} */
        this._targetBubbleFill = new GameAnimatedSprite(null, Game.design.getGraphicData("TargetBubbleFill"));
        this._rootAllOut.addChild(this._targetBubbleFill);

        /** @private @type {GameSprite} */
        this._targetBubbleIndicator = new GameSprite(AIMLINE_TARGET_INDICATORS[0]);
        this._rootAllOut.addChild(this._targetBubbleIndicator);

        /** @private @type {import("../Constants").CollisionData} */
        this._result = {
            hit: false,
            hitBubble: false,
            hitWall: false,
            hitSideWall: false,
            hitBackWall: false,
            bubble: null,
            contactX: 0,
            contactY: 0
        };

        /** @private @type {import("../Constants").CollisionData} */
        this._resultRebound = {
            hit: false,
            hitBubble: false,
            hitWall: false,
            hitSideWall: false,
            hitBackWall: false,
            bubble: null,
            contactX: 0,
            contactY: 0
        };

        Game.on(GameEvents.eHEROES_ALL_OUT_ATTACK, this._onAllOutAttack, this);
        Game.on(GameEvents.eHEROES_ALL_OUT_ATTACK_BUFF_COMPLETED, this._onAllOutAttackCompleted, this);
    }

    /**
     * @public
     * @param {Number} deltaTime
     */
    updateLoop(deltaTime) {
        if (!this._root.visible || Game.isPaused()) {
            return;
        }

        if (this._allOutActive) {
            return;
        }

        // Move all dots in distance
        // Set dots positions and visibility
        const opacityDistance = this._dotsPath.totalDistance - AIMLINE_DOTS_FADE_DISTANCE;
        for (const dot of this._dots) {
            dot.distance += AIMLINE_DOTS_SPEED * deltaTime;
            if (dot.distance > this._maxDotDistance) {
                dot.distance -= this._maxDotDistance;
            }

            let dotVisible = dot.distance >= 0 && dot.distance <= this._dotsPath.totalDistance;

            dot.sprite.visible = dotVisible;
            if (dotVisible) {
                if (dot.distance <= this._dotsPath.p1.distance) {
                    // First segment
                    dot.sprite.x = SHOOTER_X_POSITION + this._dotsPath.p1.cos * dot.distance;
                    dot.sprite.y = SHOOTER_Y_POSITION + this._dotsPath.p1.sin * dot.distance;
                } else {
                    // Rebound segment
                    let reboundDistance = dot.distance - this._dotsPath.p1.distance;
                    dot.sprite.x = this._dotsPath.p1.x + this._dotsPath.p2.cos * reboundDistance;
                    dot.sprite.y = this._dotsPath.p1.y + this._dotsPath.p2.sin * reboundDistance;
                }

                // Opacity
                dot.sprite.alpha = 1;
                if (dot.distance > opacityDistance) {
                    dot.sprite.alpha = 1 - (dot.distance - opacityDistance) / AIMLINE_DOTS_FADE_DISTANCE;
                }
            }
        }
    }

    /**
     * @public
     * @param {BubbleColor} color
     */
    setColor(color) {
        this._color = Math.min(color, BubbleColor.eSPECIAL);
        for (const dot of this._dots) {
            dot.sprite.texture = PIXI.Texture.from(AIMLINE_DOTS_SPRITES[this._color]);
        }

        if (this._allOutActive) {
            this._updateAllOutColor();
        }
    }

    /**
     * @private
     */
    _updateAllOutColor() {
        for (const laserSegment of this._lasers) {
            laserSegment.playAnimation(BUBBLE_COLOR_ID_TO_NAME[this._color]);
        }

        this._targetBubbleIndicator.texture = PIXI.Texture.from(AIMLINE_TARGET_INDICATORS[this._color]);

        if (this._color < BubbleColor.eSPECIAL) {
            this._visor.tint = BUBBLE_TINTS[this._color];
        } else {
            this._visor.tint = 0xffffff;
        }
    }

    /**
     * @private
     */
    _onAllOutAttack() {
        this._allOutActive = true;
        this._updateAllOutColor();

        this._rootDots.visible = false;
        this._rootAllOut.visible = true;

        this._targetBubbleFill.playAnimation("idle", true);
    }

    /**
     * @private
     */
    _onAllOutAttackCompleted() {
        this._allOutActive = false;

        this._rootDots.visible = true;
        this._rootAllOut.visible = false;

        this._targetBubbleFill.stop();
    }

    /**
     * @public
     * @param {Boolean} visible
     * @param {Number} angle
     */
    update(visible, angle) {
        this._root.visible = visible;

        if (!visible) {
            return;
        }

        if (this._allOutActive) {
            this._updateAllOut(angle);
        } else {
            this._updateDots(angle);
        }
    }

    /**
     * @private
     * @param {Number} angle
     */
    _updateDots(angle) {
        const aimLineLength = Common.settings.aimLine.length;

        const angleRadians = (angle * Math.PI) / 180;
        let cos = Math.cos(angleRadians);
        let sin = Math.sin(angleRadians);
        let endX = SHOOTER_X_POSITION + cos * aimLineLength;
        let endY = SHOOTER_Y_POSITION + sin * aimLineLength;

        const collisionRadius = Common.settings.collisionRadius;
        Common.collisions.bubbleHitTest(SHOOTER_X_POSITION, SHOOTER_Y_POSITION, endX, endY, collisionRadius, this._result);

        this._dotsPath.p1.x = endX;
        this._dotsPath.p1.y = endY;
        this._dotsPath.p1.distance = aimLineLength;
        this._dotsPath.p1.cos = cos;
        this._dotsPath.p1.sin = sin;

        if (this._result.hit) {
            // Stops the aim line at contact point
            const deltaX = this._result.contactX - SHOOTER_X_POSITION;
            const deltaY = this._result.contactY - SHOOTER_Y_POSITION;
            const distanceToContact = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            endX = SHOOTER_X_POSITION + cos * distanceToContact;
            endY = SHOOTER_Y_POSITION + sin * distanceToContact;

            this._dotsPath.p1.x = endX;
            this._dotsPath.p1.y = endY;
            this._dotsPath.p1.distance = distanceToContact;
        }

        // No rebound ==> p2 is the same as p1
        this._dotsPath.p2.x = this._dotsPath.p1.x;
        this._dotsPath.p2.y = this._dotsPath.p1.y;
        this._dotsPath.p2.distance = 0;

        // Rebound on side wall?
        if (this._result.hitSideWall) {
            const reboundLineLength = Common.settings.aimLine.reboundLength;

            let reboundStartX = this._result.contactX;
            let reboundStartY = this._result.contactY;
            let reboundCos = Math.cos(-angleRadians);
            let reboundSin = Math.sin(-angleRadians);
            let reboundEndX = reboundStartX + reboundCos * reboundLineLength;
            let reboundEndY = reboundStartY + reboundSin * reboundLineLength;

            this._dotsPath.p2.x = reboundEndX;
            this._dotsPath.p2.y = reboundEndY;
            this._dotsPath.p2.distance = reboundLineLength;
            this._dotsPath.p2.cos = reboundCos;
            this._dotsPath.p2.sin = reboundSin;

            const collisionRadius = Common.settings.collisionRadius;
            Common.collisions.bubbleHitTest(reboundStartX, reboundStartY, reboundEndX, reboundEndY, collisionRadius, this._resultRebound);

            if (this._resultRebound.hit) {
                // Stops the aim line at contact point
                const deltaX = this._resultRebound.contactX - reboundStartX;
                const deltaY = this._resultRebound.contactY - reboundStartY;
                const reboundDistanceToContact = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                reboundEndX = reboundStartX + reboundCos * reboundDistanceToContact;
                reboundEndY = reboundStartY + reboundSin * reboundDistanceToContact;

                this._dotsPath.p2.x = reboundEndX;
                this._dotsPath.p2.y = reboundEndY;
                this._dotsPath.p2.distance = reboundDistanceToContact;
            }
        }

        this._dotsPath.totalDistance = this._dotsPath.p1.distance + this._dotsPath.p2.distance;
    }

    /**
     * @private
     * @param {Number} angle
     */
    _updateAllOut(angle) {
        const lines = [];
        let finalX = 0;
        let finalY = 0;

        this._laserSegmentIndex = 0;
        this._patchIndex = 0;

        const aimLineLength = Common.settings.aimLine.length;
        const collisionRadius = Common.settings.collisionRadius;

        let angleRadians = (angle * Math.PI) / 180;
        let endX = SHOOTER_X_POSITION + Math.cos(angleRadians) * aimLineLength;
        let endY = SHOOTER_Y_POSITION + Math.sin(angleRadians) * aimLineLength;

        this._visor.rotation = angleRadians;

        Common.collisions.bubbleHitTest(SHOOTER_X_POSITION, SHOOTER_Y_POSITION, endX, endY, collisionRadius, this._result);

        if (this._result.hit) {
            // Stops the aim line at contact point
            const deltaX = this._result.contactX - SHOOTER_X_POSITION;
            const deltaY = this._result.contactY - SHOOTER_Y_POSITION;
            const distanceToContact = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            endX = SHOOTER_X_POSITION + Math.cos(angleRadians) * distanceToContact;
            endY = SHOOTER_Y_POSITION + Math.sin(angleRadians) * distanceToContact;

            if (this._result.hitBubble || this._result.hitBackWall) {
                // Find the target bubble position on the grid
                let targetPosition = { x: this._result.contactX, y: this._result.contactY };
                Common.grid.clipPositionToGrid(targetPosition);

                endX = targetPosition.x;
                endY = targetPosition.y;
                finalX = targetPosition.x;
                finalY = targetPosition.y;
            }
        }
        lines.push({
            startX: SHOOTER_X_POSITION,
            startY: SHOOTER_Y_POSITION,
            endX: endX,
            endY: endY,
            addStartJunction: false,
            addEndJunction: this._result.hitSideWall
        });

        // Rebound on side wall ==> infinite rebounds until we hit a bubble of the back wall
        if (this._result.hitSideWall) {
            let reboundStartX = 0;
            let reboundStartY = 0;
            let reboundEndX = this._result.contactX;
            let reboundEndY = this._result.contactY;

            let hitBubbleOrBackWall = false;
            while (!hitBubbleOrBackWall) {
                angleRadians *= -1;
                reboundStartX = reboundEndX;
                reboundStartY = reboundEndY;
                reboundEndX = reboundStartX + Math.cos(angleRadians) * aimLineLength;
                reboundEndY = reboundStartY + Math.sin(angleRadians) * aimLineLength;

                Common.collisions.bubbleHitTest(
                    reboundStartX,
                    reboundStartY,
                    reboundEndX,
                    reboundEndY,
                    collisionRadius,
                    this._resultRebound
                );

                if (this._resultRebound.hit) {
                    // Stops the aim line at contact point
                    const deltaX = this._resultRebound.contactX - reboundStartX;
                    const deltaY = this._resultRebound.contactY - reboundStartY;
                    const reboundDistanceToContact = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                    reboundEndX = reboundStartX + Math.cos(angleRadians) * reboundDistanceToContact;
                    reboundEndY = reboundStartY + Math.sin(angleRadians) * reboundDistanceToContact;

                    hitBubbleOrBackWall = this._resultRebound.hitBubble || this._resultRebound.hitBackWall;
                    if (hitBubbleOrBackWall) {
                        // Find the target bubble position on the grid
                        let targetPosition = { x: this._resultRebound.contactX, y: this._resultRebound.contactY };
                        Common.grid.clipPositionToGrid(targetPosition);

                        reboundEndX = targetPosition.x;
                        reboundEndY = targetPosition.y;
                        finalX = targetPosition.x;
                        finalY = targetPosition.y;
                    }
                }
                lines.push({
                    startX: reboundStartX,
                    startY: reboundStartY,
                    endX: reboundEndX,
                    endY: reboundEndY,
                    addStartJunction: true,
                    addEndJunction: this._resultRebound.hitSideWall
                });
            }
        }

        // Backtrack last line segment?
        // * Edge-case where the snapped bubble position is behind the last line segment
        //   or where the last line segment is too small, creating a visual glitch
        if (lines.length > 1) {
            const lastLine = lines[lines.length - 1];
            const beforeLastLine = lines[lines.length - 2];

            const deltaX = lastLine.endX - lastLine.startX;
            const deltaY = lastLine.endY - lastLine.startY;
            const lineLength = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            if (lastLine.startX > finalX || lineLength < AIMLINE_LASER_SEGMENT_THRESHOLD) {
                lines.pop();

                beforeLastLine.endX = finalX;
                beforeLastLine.endY = finalY;
                beforeLastLine.addEndJunction = false;
            }
        }

        for (const line of lines) {
            this._addLaserLine(line.startX, line.startY, line.endX, line.endY, line.addStartJunction, line.addEndJunction);
        }

        this._targetBubbleIndicator.x = finalX;
        this._targetBubbleIndicator.y = finalY;
        this._targetBubbleFill.x = finalX;
        this._targetBubbleFill.y = finalY;

        this._hideRemainingLaserSegments();
        this._hideRemainingPatches();
    }

    /**
     * @private
     * @param {Number} fromX
     * @param {Number} fromY
     * @param {Number} toX
     * @param {Number} toY
     * @param {Boolean} addStartJunction
     * @param {Boolean} addEndJunction
     */
    _addLaserLine(fromX, fromY, toX, toY, addStartJunction, addEndJunction) {
        const deltaX = toX - fromX;
        const deltaY = toY - fromY;
        let lineLength = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // No end junction ==> truncate to fit with target bubble indicator
        if (!addEndJunction) {
            lineLength -= AIMLINE_LAST_LASER_SEGMENT_TRUNCATE;
        }

        const angle = Math.atan2(deltaY, deltaX);
        const cosX = Math.cos(angle);
        const sinY = Math.sin(angle);

        const nbSegments = Math.ceil(lineLength / AIMLINE_LASER_SEGMENT_LENGTH);
        const segmentLength = lineLength / nbSegments;

        for (let i = 0; i < nbSegments; ++i) {
            const laserSegment = this._lasers[this._laserSegmentIndex];
            laserSegment.visible = true;

            laserSegment.x = fromX + i * segmentLength * cosX;
            laserSegment.y = fromY + i * segmentLength * sinY;
            laserSegment.rotation = angle;
            laserSegment.scale.x = segmentLength / AIMLINE_LASER_SEGMENT_LENGTH;

            ++this._laserSegmentIndex;
        }

        if (addStartJunction) {
            const patch = this._patches[this._patchIndex];
            patch.visible = true;

            patch.x = fromX;
            patch.y = fromY;
            patch.rotation = angle;

            ++this._patchIndex;
        }

        if (addEndJunction) {
            const patch = this._patches[this._patchIndex];
            patch.visible = true;

            patch.x = toX;
            patch.y = toY;
            patch.rotation = angle + Math.PI;

            ++this._patchIndex;
        }
    }

    /**
     * @private
     */
    _hideRemainingLaserSegments() {
        for (let i = this._laserSegmentIndex; i < this._lasers.length; ++i) {
            this._lasers[i].visible = false;
        }
    }

    /**
     * @private
     */
    _hideRemainingPatches() {
        for (let i = this._patchIndex; i < this._patches.length; ++i) {
            this._patches[i].visible = false;
        }
    }

    /**
     * @public
     */
    destroy() {
        for (const dot of this._dots) {
            if (!dot.sprite.destroyed) {
                dot.sprite.destroy();
            }
        }
        this._dots = [];

        for (const laserSegment of this._lasers) {
            if (!laserSegment.destroyed) {
                laserSegment.destroy();
            }
        }
        this._lasers = [];

        for (const patch of this._patches) {
            if (!patch.destroyed) {
                patch.destroy();
            }
        }
        this._patches = [];

        if (this._targetBubbleIndicator) {
            if (!this._targetBubbleIndicator.destroyed) {
                this._targetBubbleIndicator.destroy();
            }
        }
        this._targetBubbleIndicator = null;

        if (this._targetBubbleFill) {
            if (!this._targetBubbleFill.destroyed) {
                this._targetBubbleFill.destroy();
            }
        }
        this._targetBubbleFill = null;

        this._visor = null;

        if (this._root) {
            this._root.destroy();
        }
        this._root = null;

        Game.off(GameEvents.eHEROES_ALL_OUT_ATTACK, this._onAllOutAttack, this);
        Game.off(GameEvents.eHEROES_ALL_OUT_ATTACK_BUFF_COMPLETED, this._onAllOutAttackCompleted, this);
    }
}
